import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Checkbox,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  VerifiedUser as VerifiedUserIcon,
  People as PeopleIcon,
  LockOpen,
  VpnKey as Key,
  ExitToApp,
} from "@mui/icons-material";
import { formatApiError } from "../../utils/formatApiError";

import Currency from "../../components/common/Currency";
import UserDetailModal from "../../components/backend/user/UserDetailModal";
import UserFormModal from "../../components/backend/user/UserFormModal";
import UserFiltersPanel from "../../components/backend/user/UserFiltersPanel";
import { exportUsers, downloadFile } from "../../services/adminUserService";
import {
  AdminUser,
  UserFilters,
  UserModalState,
  UserModalMode,
} from "../../types/admin";
import {
  useUsers,
  useUserStatistics,
  useUserDetail,
  useUserMutations,
  useOptimisticUserUpdate,
} from "../../hooks/useAdminUsers";

import AdminPage from "./AdminPage";

const UsersPage: React.FC = () => {
  const { t } = useTranslation();

  // State for filters and pagination
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    pageSize: 10,
    sortBy: "createdAt",
    sortDirection: "desc",
  });

  // React Query hooks
  const {
    data: usersData,
    isLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useUsers(filters);

  const {
    data: statisticsData,
    isLoading: statisticsLoading,
    error: statisticsError,
    refetch: refetchStatistics,
  } = useUserStatistics();

  // Extract data from React Query responses
  const users = usersData?.data?.users || [];
  const totalCount = usersData?.data?.totalCount || 0;
  const statistics = statisticsData?.data || null;

  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // User mutations
  const userMutations = useUserMutations();
  const {
    unlockUser,
    resetPassword,
    revokeAllSessions,
    bulkActivate,
    bulkDeactivate,
    bulkAssignRole,
  } = userMutations;
  const { updateUserOptimistically } = useOptimisticUserUpdate();

  // State for modals
  const [modalState, setModalState] = useState<UserModalState>({
    open: false,
    mode: "view",
  });

  // State for user actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Reset password dialog state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // State for notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // For user detail modal - only fetch when needed
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { data: userDetailData, isLoading: userDetailLoading } = useUserDetail(
    selectedUserId || "",
    !!selectedUserId,
  );

  // Utility functions
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Modal handlers
  const openModal = (mode: UserModalMode, user?: AdminUser) => {
    if (mode === "view" || mode === "edit") {
      // Set user ID to trigger React Query fetch
      setSelectedUserId(user!.id);
      setModalState({ open: true, mode });
    } else {
      setModalState({ open: true, mode });
    }
  };

  const closeModal = () => {
    setModalState({ open: false, mode: "view" });
    setSelectedUserId(null); // Clear selected user ID to stop fetching
  };

  // User action handlers
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    user: AdminUser,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleUserAction = async (action: string, userId: string) => {
    try {
      const user = users.find((u: AdminUser) => u.id === userId);
      if (!user) return;

      switch (action) {
        case "activate":
          // Optimistic update
          updateUserOptimistically(userId, { isActive: true });
          await userMutations.activateUser.mutateAsync(userId);
          showSnackbar(t("admin.users.userActivated"));
          break;
        case "deactivate":
          // Optimistic update
          updateUserOptimistically(userId, { isActive: false });
          await userMutations.deactivateUser.mutateAsync(userId);
          showSnackbar(t("admin.users.userDeactivated"));
          break;
        case "verify-email":
          // Optimistic update
          updateUserOptimistically(userId, { isEmailVerified: true });
          await userMutations.verifyEmail.mutateAsync(userId);
          showSnackbar(t("admin.users.emailVerified"));
          break;
        default:
          break;
      }

      handleMenuClose();
    } catch (error) {
      showSnackbar(t("admin.users.actionError", { action }), "error");
      // React Query will automatically refetch on error, reverting optimistic updates
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await userMutations.deleteUser.mutateAsync(selectedUser.id);
      showSnackbar(t("admin.users.userDeleted"));
      setDeleteDialogOpen(false);
      handleMenuClose();
    } catch (error) {
      showSnackbar(t("admin.users.deleteError"), "error");
    }
  };

  // New feature handlers
  const handleUnlockUser = async (userId: string) => {
    try {
      await unlockUser.mutateAsync(userId);
      showSnackbar(t("admin.users.userUnlocked"));
      handleMenuClose();
    } catch (error) {
      showSnackbar(formatApiError(error), "error");
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUserId || !newPassword) return;
    try {
      await resetPassword.mutateAsync({
        userId: resetPasswordUserId,
        data: { newPassword, confirmNewPassword: confirmPassword },
      });
      showSnackbar(t("admin.users.passwordResetSuccess"));
      setResetPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
      setResetPasswordUserId(null);
    } catch (error) {
      showSnackbar(formatApiError(error), "error");
    }
  };

  const handleRevokeAllSessions = async (userId: string) => {
    try {
      await revokeAllSessions.mutateAsync(userId);
      showSnackbar(t("admin.users.sessionsRevoked"));
      handleMenuClose();
    } catch (error) {
      showSnackbar(formatApiError(error), "error");
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedUserIds.length === 0) return;
    try {
      const request = { userIds: selectedUserIds };
      switch (action) {
        case "activate":
          await bulkActivate.mutateAsync(request);
          break;
        case "deactivate":
          await bulkDeactivate.mutateAsync(request);
          break;
      }
      showSnackbar(t("admin.users.bulkActionSuccess"));
      setSelectedUserIds([]);
    } catch (error) {
      showSnackbar(formatApiError(error), "error");
    }
  };

  const handleToggleSelectUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map((u: AdminUser) => u.id));
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportUsers(filters);
      downloadFile(
        blob,
        `users_export_${new Date().toISOString().split("T")[0]}.csv`,
      );
      showSnackbar(t("admin.users.exportSuccess"));
    } catch (error) {
      showSnackbar(t("admin.users.exportError"), "error");
    }
  };

  // Table handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFilters((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 1,
    }));
  };

  // Helper functions
  const getStatusColor = (isActive: boolean) => {
    return isActive ? "success" : "error";
  };

  const getRoleColor = (roles: string[]) => {
    if (roles.some((role) => role === "Admin")) return "primary";
    return "default";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <AdminPage>
      <Box>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4">{t("admin.users.title")}</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={() => openModal("create")}
          >
            {t("admin.users.addNewUser")}
          </Button>
        </Box>

        {/* Enhanced Statistics Cards */}
        {statistics && (
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  position: "relative",
                  overflow: "visible",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  borderRadius: 2,
                  boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                }}
              >
                <CardContent sx={{ pb: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontWeight: 500,
                          mb: 1,
                        }}
                      >
                        {t("admin.users.totalUsers")}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        {statistics.totalUsers.toLocaleString()}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.9)" }}
                        >
                          {t("admin.users.vsLastMonth", { percent: "+12%" })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 32 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  position: "relative",
                  overflow: "visible",
                  background:
                    "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  color: "white",
                  borderRadius: 2,
                  boxShadow: "0 8px 32px rgba(17, 153, 142, 0.3)",
                }}
              >
                <CardContent sx={{ pb: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontWeight: 500,
                          mb: 1,
                        }}
                      >
                        {t("admin.users.activeUsers")}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        {statistics.activeUsers.toLocaleString()}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.9)" }}
                        >
                          {t("admin.users.vsLastMonth", { percent: "+8%" })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 32 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  position: "relative",
                  overflow: "visible",
                  background:
                    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                  color: "white",
                  borderRadius: 2,
                  boxShadow: "0 8px 32px rgba(250, 112, 154, 0.3)",
                }}
              >
                <CardContent sx={{ pb: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          fontWeight: 500,
                          mb: 1,
                        }}
                      >
                        {t("admin.users.verifiedUsers")}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        {statistics.verifiedUsers.toLocaleString()}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255, 255, 255, 0.9)" }}
                        >
                          {t("admin.users.vsLastMonth", { percent: "+15%" })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <VerifiedUserIcon sx={{ fontSize: 32 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  position: "relative",
                  overflow: "visible",
                  background:
                    "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
                  color: "#374151",
                  borderRadius: 2,
                  boxShadow: "0 8px 32px rgba(168, 237, 234, 0.3)",
                }}
              >
                <CardContent sx={{ pb: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(55, 65, 81, 0.7)",
                          fontWeight: 500,
                          mb: 1,
                        }}
                      >
                        {t("admin.users.recentUsers")}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: "#374151",
                        }}
                      >
                        {statistics.recentUsers.toLocaleString()}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(55, 65, 81, 0.8)" }}
                        >
                          {t("admin.users.last30Days")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        backgroundColor: "rgba(55, 65, 81, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PersonAddIcon sx={{ fontSize: 32, color: "#374151" }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <UserFiltersPanel
          filters={filters}
          onFiltersChange={setFilters}
          onExport={handleExport}
          loading={isLoading}
        />

        {/* Bulk Actions Toolbar */}
        {selectedUserIds.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              p: 1.5,
              bgcolor: "action.selected",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              {t("admin.users.selectedCount", {
                count: selectedUserIds.length,
              })}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => handleBulkAction("activate")}
            >
              {t("admin.users.bulkActivate")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => handleBulkAction("deactivate")}
            >
              {t("admin.users.bulkDeactivate")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedUserIds([])}
            >
              {t("admin.users.clearSelection")}
            </Button>
          </Box>
        )}

        {/* Users Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedUserIds.length > 0 &&
                        selectedUserIds.length < users.length
                      }
                      checked={
                        users.length > 0 &&
                        selectedUserIds.length === users.length
                      }
                      onChange={handleToggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>{t("admin.users.user")}</TableCell>
                  <TableCell>{t("admin.users.contact")}</TableCell>
                  <TableCell>{t("admin.users.roles")}</TableCell>
                  <TableCell>{t("admin.users.status")}</TableCell>
                  <TableCell>{t("admin.users.registration")}</TableCell>
                  <TableCell>{t("admin.users.orders")}</TableCell>
                  <TableCell>{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.users.noUsersFound")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user: AdminUser) => (
                    <TableRow key={user.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleToggleSelectUser(user.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar src={user.profilePicture}>
                            {user.firstName?.[0]}
                            {user.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {user.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {user.id.slice(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {user.email}
                            {user.isEmailVerified && (
                              <VerifiedUserIcon
                                color="success"
                                fontSize="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          {user.phoneNumber && (
                            <Typography variant="body2" color="text.secondary">
                              {user.phoneNumber}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {user.authType}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {user.roles.map((role, index) => (
                            <Chip
                              key={index}
                              label={role}
                              size="small"
                              color={getRoleColor(user.roles) as any}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Chip
                            label={user.isActive ? t("admin.users.active") : t("admin.users.inactive")}
                            size="small"
                            color={getStatusColor(user.isActive) as any}
                          />
                          {!user.isEmailVerified && (
                            <Chip
                              label={t("admin.users.unverified")}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(user.createdAt)}
                        </Typography>
                        {user.lastLoginAt && (
                          <Typography variant="caption" color="text.secondary">
                            {t("admin.users.lastLogin", { date: formatDate(user.lastLoginAt) })}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {t("admin.users.orderCount", { count: user.totalOrders || 0 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <Currency amount={user.totalSpent || 0} />
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Tooltip title={t("admin.users.viewDetails")}>
                            <IconButton
                              size="small"
                              onClick={() => openModal("view", user)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t("admin.users.editUser")}>
                            <IconButton
                              size="small"
                              onClick={() => openModal("edit", user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, user)}
                            disabled={
                              userMutations.activateUser.isPending ||
                              userMutations.deactivateUser.isPending ||
                              userMutations.verifyEmail.isPending ||
                              userMutations.deleteUser.isPending
                            }
                          >
                            {(userMutations.activateUser.isPending ||
                              userMutations.deactivateUser.isPending ||
                              userMutations.verifyEmail.isPending ||
                              userMutations.deleteUser.isPending) &&
                            selectedUser?.id === user.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <MoreVertIcon />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={filters.pageSize || 10}
            page={(filters.page || 1) - 1}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Paper>

        {/* User Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {selectedUser && !selectedUser.isActive && (
            <MenuItem
              onClick={() => handleUserAction("activate", selectedUser.id)}
            >
              <CheckCircleIcon sx={{ mr: 1 }} />
              {t("admin.users.activateUser")}
            </MenuItem>
          )}

          {selectedUser && selectedUser.isActive && (
            <MenuItem
              onClick={() => handleUserAction("deactivate", selectedUser.id)}
            >
              <CancelIcon sx={{ mr: 1 }} />
              {t("admin.users.deactivateUser")}
            </MenuItem>
          )}

          {selectedUser && !selectedUser.isEmailVerified && (
            <MenuItem
              onClick={() => handleUserAction("verify-email", selectedUser.id)}
            >
              <EmailIcon sx={{ mr: 1 }} />
              {t("admin.users.verifyEmail")}
            </MenuItem>
          )}

          {selectedUser && (
            <MenuItem
              onClick={() => handleUnlockUser(selectedUser.id)}
            >
              <LockOpen sx={{ mr: 1 }} fontSize="small" />
              {t("admin.users.unlockUser")}
            </MenuItem>
          )}

          {selectedUser && (
            <MenuItem
              onClick={() => {
                setResetPasswordUserId(selectedUser.id);
                setResetPasswordDialogOpen(true);
                handleMenuClose();
              }}
            >
              <Key sx={{ mr: 1 }} fontSize="small" />
              {t("admin.users.resetPassword")}
            </MenuItem>
          )}

          {selectedUser && (
            <MenuItem
              onClick={() => handleRevokeAllSessions(selectedUser.id)}
            >
              <ExitToApp sx={{ mr: 1 }} fontSize="small" />
              {t("admin.users.revokeSessions")}
            </MenuItem>
          )}

          <MenuItem onClick={() => setDeleteDialogOpen(true)}>
            <DeleteIcon sx={{ mr: 1 }} color="error" />
            {t("admin.users.deleteUser")}
          </MenuItem>
        </Menu>

        {/* Modals */}
        <UserDetailModal
          open={modalState.open && modalState.mode === "view"}
          user={userDetailLoading ? null : userDetailData?.data || null}
          onClose={closeModal}
          onEdit={(user) => setModalState({ open: true, mode: "edit", user })}
        />

        <UserFormModal
          open={
            modalState.open &&
            (modalState.mode === "create" || modalState.mode === "edit")
          }
          mode={modalState.mode}
          user={
            modalState.mode === "edit"
              ? userDetailData?.data || undefined
              : undefined
          }
          onClose={closeModal}
          onSuccess={async () => {
            // React Query will automatically refetch the data
            closeModal();
            showSnackbar(
              modalState.mode === "create"
                ? t("admin.users.userCreated")
                : t("admin.users.userUpdated"),
            );
          }}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>{t("admin.users.deleteUser")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("admin.users.deleteConfirmation", { name: selectedUser?.fullName })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={handleDeleteUser}
              disabled={userMutations.deleteUser.isPending}
              color="error"
              variant="contained"
              startIcon={
                userMutations.deleteUser.isPending ? (
                  <CircularProgress size={16} />
                ) : null
              }
            >
              {t("common.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog
          open={resetPasswordDialogOpen}
          onClose={() => setResetPasswordDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("admin.users.resetPasswordTitle")}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label={t("admin.users.newPassword")}
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label={t("admin.users.confirmPassword")}
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetPasswordDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              disabled={
                !newPassword ||
                newPassword !== confirmPassword ||
                newPassword.length < 8
              }
            >
              {t("admin.users.resetPassword")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default UsersPage;
